import type { ErrorLike } from '@apollo/client';
import { useCallback } from 'react';

import {
  type AtlasGqlTableView,
  AtlasGqlViewType,
  useGetBladeCardsLazyQuery,
  useGetBladeCardsQuery,
  useGetBladeDefectPatternsLazyQuery,
  useGetBladeDefectPatternsQuery,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { DEFECT_PATTERNS } from 'utils/release-toggles';

type BladeCardViewsResult = {
  views: AtlasGqlTableView[];
  loading: boolean;
  error?: ErrorLike;
};

export type BladeCardViewCustomFields = {
  bladeMake?: string;
  bladeModel?: string;
  [key: string]: unknown;
};

export const isBladeCardViewType = (type?: AtlasGqlViewType | null): boolean =>
  type === AtlasGqlViewType.BladeDefectPattern || type === AtlasGqlViewType.BladeHotZone;

// A Turbine-matched Defect Pattern has no Blade make/model, so it names a representative
// Blade to scale diagrams against. Half a pair reads as absent, never mixed with filters.
export const getRepresentativeBlade = (
  view?: AtlasGqlTableView | null
): { make: string; model: string } | undefined => {
  const { bladeMake, bladeModel } = (view?.customFields as BladeCardViewCustomFields | null) ?? {};
  return bladeMake && bladeModel ? { make: bladeMake, model: bladeModel } : undefined;
};

export const useBladeCardViews = (): BladeCardViewsResult => {
  const { hasReleaseToggle } = useAccountContext();
  const hasDefectPatterns = hasReleaseToggle(DEFECT_PATTERNS);

  const hotZones = useGetBladeCardsQuery({ skip: hasDefectPatterns });
  const defectPatterns = useGetBladeDefectPatternsQuery({ skip: !hasDefectPatterns });

  return hasDefectPatterns
    ? {
        views: defectPatterns.data?.bladeDefectPatternTableViews ?? [],
        loading: defectPatterns.loading,
        error: defectPatterns.error,
      }
    : {
        views: hotZones.data?.bladeHotZoneTableViews ?? [],
        loading: hotZones.loading,
        error: hotZones.error,
      };
};

export const useBladeCardViewsLazy = (): (() => Promise<AtlasGqlTableView[]>) => {
  const { hasReleaseToggle } = useAccountContext();
  const hasDefectPatterns = hasReleaseToggle(DEFECT_PATTERNS);

  const [loadHotZones] = useGetBladeCardsLazyQuery();
  const [loadDefectPatterns] = useGetBladeDefectPatternsLazyQuery();

  return useCallback(async () => {
    if (hasDefectPatterns) {
      const { data } = await loadDefectPatterns();
      return data?.bladeDefectPatternTableViews ?? [];
    }
    const { data } = await loadHotZones();
    return data?.bladeHotZoneTableViews ?? [];
  }, [hasDefectPatterns, loadDefectPatterns, loadHotZones]);
};
